import React, { useRef, useState } from "react";

import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import millify from "millify";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import AirspaceCharacterizationMapPreview from "./airspaceCharacterizationMapPreview";

import { BarChart } from "@mui/x-charts/BarChart";
import { getFeaturesFromSubmissionAndType, getMapLegendFromSubmission, getDateRangeFromIsoStrings } from "./airspaceCharacterizationUtil";

const AirspaceCharacterizationReport = ({ selectedReport, setSelectedReport }) => {
    const [selectedHistogramJson, setSelectedHistogramJson] = useState(null);
    const reportRef = useRef();

    const handleDownloadReportButtonClick = async () => {
        const element = reportRef.current;

        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "px",
            format: [canvas.width, canvas.height]
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${selectedReport.uuid}.pdf`);
    };

    return (
        <Paper ref={reportRef} sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, m: 2, maxWidth: "1200px" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Typography variant="h6" sx={{ mr: "auto" }}>
                    Airspace Characterization
                    <Typography color={"text.secondary"}>{dayjs(selectedReport.date_created).format("MMMM D, YYYY [at] h:mm a")}</Typography>
                </Typography>

                <Tooltip title="Download as PDF">
                    <IconButton onClick={() => handleDownloadReportButtonClick()}>
                        <DownloadIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Close">
                    <IconButton onClick={() => setSelectedReport(null)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                    <Typography variant="body1">Submission Criteria</Typography>

                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color={"text.secondary"}>
                                Date Range
                            </Typography>
                            <Typography variant="body1">
                                {getDateRangeFromIsoStrings(selectedReport.report_json.timestamp_start, selectedReport.report_json.timestamp_end)}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color={"text.secondary"}>
                                Source Type
                            </Typography>
                            <Typography variant="body1">{selectedReport.report_json.source_type}</Typography>
                        </Grid>

                        {Array.isArray(selectedReport.report_json.source_ids) ? (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="body2" color={"text.secondary"}>
                                    Source Ids
                                </Typography>
                                <Typography variant="body1">{selectedReport.report_json.source_ids.join(", ")}</Typography>
                            </Grid>
                        ) : (
                            <></>
                        )}
                        {[
                            { title: "Minimum Latitude", value: selectedReport.report_json.lat_min },
                            { title: "Maximum Latitude", value: selectedReport.report_json.lat_max },
                            { title: "Minimum Longitude", value: selectedReport.report_json.lon_min },
                            { title: "Maximum Longitude", value: selectedReport.report_json.lon_max }
                        ].map(({ title, value }, i) => (
                            <Grid key={i} size={{ xs: 12, md: 6 }}>
                                <Typography variant="body2" color={"text.secondary"}>
                                    {title}
                                </Typography>
                                <Typography variant="body1">{value}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                    <Typography variant="body1">Histograms</Typography>

                    <Grid container>
                        {selectedReport.report_json.histogram_jsons.map((json, i) => (
                            <Grid
                                key={i}
                                size={{ xs: 12, md: 6 }}
                                sx={{ height: "175px", p: 0, cursor: "pointer" }}
                                onClick={() => setSelectedHistogramJson(json)}
                            >
                                <BarChart
                                    xAxis={[{ scaleType: "band", data: json.bins.map((bin) => bin.bin_min), label: json.name }]}
                                    series={[{ data: json.bins.map(({ value }) => value || 0) }]}
                                    yAxis={[{ valueFormatter: (value) => millify(value) }]}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>

            <Box>
                <Typography variant="body1" gutterBottom>
                    Heatmaps
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {[
                        {
                            title: "REPORT_OCCUPANCY-COUNT",
                            features: getFeaturesFromSubmissionAndType(selectedReport, "REPORT_OCCUPANCY-COUNT")
                        },
                        {
                            title: "TRACK_OCCUPANCY-COUNT",
                            features: getFeaturesFromSubmissionAndType(selectedReport, "TRACK_OCCUPANCY-COUNT")
                        }
                    ].map(({ title, features }, i) => (
                        <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 1, justifyContent: "center", alignItems: "center" }}>
                            <Box sx={{ height: "300px", width: "100%" }}>
                                <AirspaceCharacterizationMapPreview id={title} features={features} />
                            </Box>

                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: { xs: "column-reverse", md: "row" },
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}
                            >
                                <Typography variant="body2">{title}</Typography>

                                <Box sx={{ display: "flex", flexDirection: "row", width: { xs: "100%", md: "50%" } }}>
                                    {getMapLegendFromSubmission(selectedReport, title).map(({ value, color }, index) => (
                                        <Box key={index} sx={{ flex: 1 }}>
                                            <Box sx={{ height: "8px", width: "100%", bgcolor: color, border: "1px solid white" }} />
                                            <Typography variant="caption">{value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {selectedHistogramJson ? (
                <Dialog open={true} onClose={() => setSelectedHistogramJson(null)} fullWidth maxWidth={"sm"}>
                    <DialogTitle>{selectedHistogramJson.name}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ height: "275px" }}>
                            <BarChart
                                xAxis={[{ scaleType: "band", data: selectedHistogramJson.bins.map((bin) => bin.bin_min), label: name }]}
                                series={[{ data: selectedHistogramJson.bins.map(({ value }) => value || 0) }]}
                                yAxis={[{ valueFormatter: (value) => millify(value) }]}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedHistogramJson(null)}>{"Close"}</Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <></>
            )}
        </Paper>
    );
};

export default AirspaceCharacterizationReport;
